import { useQuery } from '@tanstack/react-query'
import { MessageSquare } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { buildFeishuChatAppLink, openExternalUrl, trpc } from '@maxus/website/lib'
import { cn } from '@maxus/website/utils'

import {
  Avatar,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Skeleton,
} from '../../base'

type UserCardProps = {
  // 被展示用户的 biz_user.id。缺省（历史数据只留了姓名字符串）时退化为纯文本，不可点。
  userId?: string | null
  // 作为 <Trans> 的插值组件使用时由 i18next 注入，故声明为可选。
  children?: ReactNode
  className?: string
}

// 卡片数据在首次打开时才请求；同一 userId 的结果在 react-query 里缓存 5 分钟，
// 避免同一页面反复点开同一个人时重复打后端（其中含一次飞书 open_id 换取）。
const CARD_STALE_TIME_MS = 5 * 60 * 1000

/**
 * 人物卡片：包裹头像 / 姓名，点击后弹出展示头像、姓名、租户名；
 * 同租户时追加部门与邮箱；同租户且租户为飞书租户时追加「发送消息」（AppLink 拉起单聊）。
 *
 * 触发元素用 <span role="button"> 而非 <button>：多数调用点位于可点击的行 / 卡片（本身就是 button）
 * 内部，嵌套真实 button 会产生非法 HTML；同时对点击 stopPropagation，避免顺带触发行跳转。
 */
export const UserCard = ({ userId, children, className }: UserCardProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const { data, isPending, isError } = useQuery(
    trpc.project.user.card.queryOptions(
      { userId: userId ?? '' },
      { enabled: open && !!userId, staleTime: CARD_STALE_TIME_MS },
    ),
  )

  if (!userId) return <>{children}</>

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <span
          className={cn(
            'inline-flex max-w-full min-w-0 cursor-pointer items-center rounded-sm outline-none hover:underline focus-visible:ring-1 focus-visible:ring-focus-ring',
            className,
          )}
          onClick={event => event.stopPropagation()}
          // span 没有原生按钮的键盘激活，这里手动补 Enter / Space；同时拦截冒泡，
          // 避免顺带触发所在行 / 卡片的键盘操作。
          onKeyDown={event => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            event.stopPropagation()
            setOpen(prev => !prev)
          }}
          role="button"
          tabIndex={0}
        >
          {children}
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[268px] p-4"
        collisionPadding={12}
        onClick={event => event.stopPropagation()}
      >
        {isError ? (
          <div className="text-xs text-text-tertiary">{t('Failed to load user')}</div>
        ) : isPending || !data ? (
          <div className="flex items-center gap-3">
            <Skeleton className="size-12 rounded-full" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
        ) : (
          <UserCardBody user={data} />
        )}
      </PopoverContent>
    </Popover>
  )
}

type UserCardData = {
  name: string
  avatarUrl: string | null
  tenantName: string
  department: string | null
  email: string | null
  feishuOpenId: string | null
}

const UserCardBody = ({ user }: { user: UserCardData }) => {
  const { t } = useTranslation()
  const { department, email, feishuOpenId } = user
  const hasDetail = !!department || !!email

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3">
        <Avatar avatarUrl={user.avatarUrl} classNames={{ root: 'size-12' }} name={user.name} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold leading-[22px] text-text-primary">{user.name}</span>
          <span className="truncate text-xs leading-[18px] text-text-tertiary">{user.tenantName}</span>
        </div>
      </div>

      {hasDetail && (
        <>
          <Separator className="my-3" />
          <dl className="flex flex-col gap-2">
            {department && <UserCardField label={t('Department')} value={department} />}
            {email && <UserCardField label={t('Email')} value={email} />}
          </dl>
        </>
      )}

      {feishuOpenId && (
        <Button
          className="mt-4 h-9 w-full"
          onClick={() => openExternalUrl(buildFeishuChatAppLink(feishuOpenId))}
          type="button"
          variant="secondary"
        >
          <MessageSquare className="size-4" />
          {t('Send message')}
        </Button>
      )}
    </div>
  )
}

const UserCardField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-start gap-2 text-xs leading-[18px]">
    <dt className="w-10 shrink-0 text-text-quiet">{label}</dt>
    <dd className="min-w-0 flex-1 wrap-anywhere text-text-secondary">{value}</dd>
  </div>
)
